import { useUser } from "@/auth/useUser";
import { useClientAccessToken } from "@/contexts/clientAccessTokenContext";
import { useMedSpaFeatureFlagsQuery } from "@/graphql/queries/medspaFeatureFlags.graphql.types";
import { useMedspaFeatureFlagsByClientAccessTokenQuery } from "@/graphql/queries/medspaFeatureFlagsByClientAccessToken.graphql.types";
import { FeatureFlags } from "@/utils/featureFlags";
import { getStripeIdsFromUserMedspa } from "@/utils/stripe";

// WARNING: DO NOT USE ON THE PUBLIC BOOKING PAGE - USE `medspaInfo.additionalPublicInfo.enabledFeatures` INSTEAD
export default function useFeatureFlags() {
  const { medspa, additionalUserDetails } = useUser();

  const { clientAccessToken: token, getTokenContext } = useClientAccessToken();

  const { stripeFinancialAccountId } =
    getStripeIdsFromUserMedspa(additionalUserDetails?.userMedspa, medspa) || {};
  const { data } = useMedSpaFeatureFlagsQuery({
    variables: {
      medspaId: medspa,
    },
    skip: !medspa,
  });

  const { data: clientAccessTokenData } =
    useMedspaFeatureFlagsByClientAccessTokenQuery({
      variables: {
        token: token as string,
      },
      skip: !token,
      ...getTokenContext(),
    });

  const { enabledFeatures = [] } =
    data?.medspaPublicAdditionalInfo ||
    clientAccessTokenData?.clientAccessTokenByPk?.client.medspa
      .additionalPublicInfo ||
    {};

  return {
    // return all `enabledFeatures` so that is can used more directly my components
    enabledFeatures,
    // boolean to check if feature flags are still loading from BE
    featureFlagsLoading: !data && !clientAccessTokenData,
    // below are specific feature flags making it easy to destructure in components and check if a feature is enabled
    balanceEnabled:
      enabledFeatures.includes(FeatureFlags.BALANCE_V1) &&
      !!stripeFinancialAccountId,
    packagesAllowRetailProducts: enabledFeatures.includes(
      FeatureFlags.PACKAGES_ALLOW_RETAIL_PRODUCTS
    ),
    messagingMaintenance: enabledFeatures.includes(
      FeatureFlags.MESSAGING_MAINTENANCE
    ),
    standingOrdersV1Enabled: enabledFeatures.includes(
      FeatureFlags.STANDING_ORDERS_V1
    ),
    affirmPaymentMethodEnabled: enabledFeatures.includes(
      FeatureFlags.AFFIRM_PAYMENT_METHOD
    ),
    venmoPaymentMethodEnabled: enabledFeatures.includes(
      FeatureFlags.VENMO_PAYMENT_METHOD
    ),
    zellePaymentMethodEnabled: enabledFeatures.includes(
      FeatureFlags.ZELLE_PAYMENT_METHOD
    ),
    invoiceSummaryUpdateServicesEnabled: enabledFeatures.includes(
      FeatureFlags.INVOICE_SUMMARY_UPDATE_SERVICES
    ),
    syncGfeV2Enabled: enabledFeatures.includes(FeatureFlags.SYNC_GFE_V2),
    marketingMessagesV1Enabled: enabledFeatures.includes(
      FeatureFlags.MARKETING_MESSAGES_V1
    ),
    commandAiEnabled: enabledFeatures.includes(FeatureFlags.COMMAND_AI),
    marketingMessagesResponseV1Enabled:
      enabledFeatures.includes(FeatureFlags.MARKETING_MESSAGES_RESPONSE_V1) &&
      enabledFeatures.includes(FeatureFlags.MARKETING_MESSAGES_V1),
    shopifyPlusEnabled: enabledFeatures.includes(FeatureFlags.SHOPIFY_PLUS),
    onlineBookingDepositsV1Enabled: enabledFeatures.includes(
      FeatureFlags.ONLINE_BOOKING_DEPOSITS_V1
    ),
    cherryIntegrationV1Enabled: enabledFeatures.includes(
      FeatureFlags.CHERRY_INTEGRATION_V1
    ),
    globalSuppliesSavingsV1Enabled: enabledFeatures.includes(
      FeatureFlags.GLOBAL_SUPPLIES_SAVINGS_V1
    ),
    payrollIntegrationV1Enabled: enabledFeatures.includes(
      FeatureFlags.PAYROLL_INTEGRATION_V1
    ),
    payrollIntegrationBenefitsNextEnabled: enabledFeatures.includes(
      FeatureFlags.PAYROLL_INTEGRATION_BENEFITS_NEXT
    ),
    payrollIntegrationBenefitsSimplyInsureEnabled: enabledFeatures.includes(
      FeatureFlags.PAYROLL_INTEGRATION_BENEFITS_SIMPLY_INSURE
    ),
    payrollIntegrationBenefitsGuidelineEnabled: enabledFeatures.includes(
      FeatureFlags.PAYROLL_INTEGRATION_BENEFITS_GUIDELINE
    ),
    payrollEmployeeTableEnabled: enabledFeatures.includes(
      FeatureFlags.PAYROLL_EMPLOYEE_TABLE
    ),
    rolesConfigurationV1Enabled: enabledFeatures.includes(
      FeatureFlags.ROLES_CONFIGURATION_V1
    ),
    stripeNinePercentIntegrationEnabled: enabledFeatures.includes(
      FeatureFlags.STRIPE_NINE_PERCENT_INTEGRATION
    ),
    packageCardRevampV1Enabled: enabledFeatures.includes(
      FeatureFlags.PACKAGE_CARD_REVAMP_V1
    ),
    improvedGfeRequestV1Enabled: enabledFeatures.includes(
      FeatureFlags.IMPROVED_GFE_REQUEST_V1
    ),
    clientPhoneOptionalEnabled: enabledFeatures.includes(
      FeatureFlags.CLIENT_PHONE_OPTIONAL
    ),
    bankingReportsV1Enabled: enabledFeatures.includes(
      FeatureFlags.BANKING_REPORTS_V1
    ),
    scriptsNavItemEnabled: enabledFeatures.includes(
      FeatureFlags.SCRIPTS_NAV_ITEM
    ),
    inventoryV3Enabled: enabledFeatures.includes(FeatureFlags.INVENTORY_V3),
    practiceAcceleratorV1Enabled: enabledFeatures.includes(
      FeatureFlags.PRACTICE_ACCELERATOR_V1
    ),
    employeesSectionEnabled: enabledFeatures.includes(
      FeatureFlags.EMPLOYEES_SECTION
    ),
    hrtLabsV1Enabled: enabledFeatures.includes(FeatureFlags.HRT_LABS_V1),
    newAppointmentModelEnabled: enabledFeatures.includes(
      FeatureFlags.NEW_APPOINTMENT_MODEL
    ),
    realTimeSalesV1Enabled: enabledFeatures.includes(
      FeatureFlags.REAL_TIME_SALES_V1
    ),
    salesAttributionV1Enabled: enabledFeatures.includes(
      FeatureFlags.SALES_ATTRIBUTION_V1
    ),
    providerColorsConfigEnabled: enabledFeatures.includes(
      FeatureFlags.PROVIDER_COLORS_CONFIG
    ),
    checkInNotificationsV1Enabled: enabledFeatures.includes(
      FeatureFlags.CHECK_IN_NOTIFICATIONS_V1
    ),
    overrideApptCommsV1Enabled: enabledFeatures.includes(
      FeatureFlags.OVERRIDE_APPT_COMMS_V1
    ),
    membershipsEnhancementsV1Enabled: enabledFeatures.includes(
      FeatureFlags.MEMBERSHIPS_ENHANCEMENTS_V1
    ),
    membershipsEnhancementsV3Enabled: enabledFeatures.includes(
      FeatureFlags.MEMBERSHIPS_ENHANCEMENTS_V3
    ),
    allowMdUploadGfeFileEnabled: enabledFeatures.includes(
      FeatureFlags.ALLOW_MD_UPLOAD_GFE_FILE
    ),
    createClientDrawerEnabled: enabledFeatures.includes(
      FeatureFlags.CREATE_CLIENT_DRAWER
    ),
    onlyRequireCcForNewClientsEnabled: enabledFeatures.includes(
      FeatureFlags.ONLY_REQUIRE_CC_FOR_NEW_CLIENTS
    ),
    medicalDataExportV1Enabled: enabledFeatures.includes(
      FeatureFlags.MEDICAL_DATA_EXPORT_V1
    ),
    cashDrawerV1Enabled: enabledFeatures.includes(FeatureFlags.CASH_DRAWER_V1),
    exclusiveToCustomBookingLinksV1Enabled: enabledFeatures.includes(
      FeatureFlags.EXCLUSIVE_TO_CUSTOM_BOOKING_LINKS_V1
    ),
    intakeFormMultiplePhotosEnabled: enabledFeatures.includes(
      FeatureFlags.INTAKE_FORM_MULTIPLE_PHOTOS
    ),
    intercomV1Enabled: enabledFeatures.includes(FeatureFlags.INTERCOM_V1),
    clockInClockOutV1Enabled: enabledFeatures.includes(
      FeatureFlags.CLOCK_IN_CLOCK_OUT_V1
    ),
    multiProviderAppointmentsV1Enabled: enabledFeatures.includes(
      FeatureFlags.MULTI_PROVIDER_APPOINTMENTS_V1
    ),
    waitlistV1Enabled: enabledFeatures.includes(FeatureFlags.WAITLIST_V1),
    clientMergeV1Enabled: enabledFeatures.includes(
      FeatureFlags.CLIENT_MERGE_V1
    ),
    externalRnOrderingV1Enabled: enabledFeatures.includes(
      FeatureFlags.EXTERNAL_RN_ORDERING_V1
    ),
    multiProvidersOnlineBookingV1Enabled: enabledFeatures.includes(
      FeatureFlags.MULTI_PROVIDERS_ONLINE_BOOKING_V1
    ),
    gfeImprovementsV1Enabled: enabledFeatures.includes(
      FeatureFlags.GFE_IMPROVEMENTS_V1
    ),
    featureManagementV1Enabled: enabledFeatures.includes(
      FeatureFlags.FEATURE_MANAGEMENT_V1
    ),
    treatmentPlansV1Enabled: enabledFeatures.includes(
      FeatureFlags.TREATMENT_PLANS_V1
    ),
    membershipsEnhancementsV4Enabled: enabledFeatures.includes(
      FeatureFlags.MEMBERSHIPS_ENHANCEMENTS_V4
    ),
    hideEventsOnMembershipSaleV1Enabled: enabledFeatures.includes(
      FeatureFlags.HIDE_EVENTS_ON_MEMBERSHIP_SALE_V1
    ),
  };
}
